import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiEdit, FiCalendar, FiFileText, FiTrash2 } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { PageHeader } from '../../components/common';
import { getNotice, deleteNotice } from '../../services/api';
import { NOTICE_CATEGORIES } from './noticesConfig';
import styles from './ViewNoticePage.module.css';

const ViewNoticePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDerasarAdmin } = useAuth();

  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  const loadNotice = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getNotice(id);
      setNotice(res.data);
    } catch (err) {
      toast.error('Failed to load notice');
      navigate('/derasar/notices');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (!id) return;
    loadNotice();
  }, [id, loadNotice]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this notice?')) return;

    setDeleting(true);
    try {
      await deleteNotice(notice.id);
      toast.success('Notice deleted');
      navigate('/derasar/notices');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete notice');
      setDeleting(false);
    }
  };

  const getCategoryLabel = (cat) => {
    return NOTICE_CATEGORIES.find((c) => c.value === cat)?.label || cat || 'Uncategorized';
  };

  const getCategoryColor = (cat) =>
    NOTICE_CATEGORIES.find((c) => c.value === cat)?.color || '#6b7280';

  if (loading) return <div className="loading"><div className="spinner" /></div>;
  if (!notice) return <div>Notice not found</div>;

  return (
    <div className={styles.container}>
      <PageHeader
        icon={<FiFileText />}
        title="Notice Details"
        actions={(
          <div className={styles.headerActions}>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => navigate('/derasar/notices')}
              title="Back to Notice Board"
            >
              <FiArrowLeft /> Back
            </button>
            {isDerasarAdmin && (
              <>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => navigate(`/derasar/notices/${notice.id}/edit`)}
                >
                  <FiEdit /> Edit
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  <FiTrash2 /> {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </>
            )}
          </div>
        )}
      />

      <div className={styles.content}>
        <div className={styles.titleSection}>
          <h1>{notice.title}</h1>
          {notice.category && (
            <span
              className={styles.categoryBadge}
              style={{ backgroundColor: getCategoryColor(notice.category) }}
            >
              {getCategoryLabel(notice.category)}
            </span>
          )}
        </div>

        <div className={styles.metaRow}>
          <div className={styles.metaItem}>
            <FiCalendar size={14} />
            Posted: {new Date(notice.createdAt).toLocaleDateString('en-IN', {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
            })}
          </div>
          {notice.expiryDate && (
            <div className={styles.metaItem}>
              <FiCalendar size={14} />
              Expires: {new Date(notice.expiryDate).toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              })}
            </div>
          )}
          <div className={styles.metaItem}>
            Status: <span className={notice.isActive ? styles.statusActive : styles.statusInactive}>
              {notice.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        <div
          className="noticeRichContent"
          dangerouslySetInnerHTML={{ __html: notice.content }}
        />
      </div>
    </div>
  );
};

export default ViewNoticePage;
